import { useEffect, useState } from "react";
import { Link, Redirect, useParams } from "wouter";
import { useAuth } from "../hooks/useAuth";
import { customerFetch } from "../lib/customerFetch";
import { Wordmark } from "../components/Brand";

/**
 * Customer Voucher detail — Phase 9.
 *
 * Route: /vouchers/:redemptionId
 * The customer hands the cashier a big readable code; the cashier
 * types it into /merchant/redeem. Snapshot fields (rewardName,
 * rewardType, rewardMetadata) come from the redemption row, so an
 * archived reward still renders correctly here.
 */

interface VoucherDetail {
  id: string;
  voucherCode: string;
  status: "ISSUED" | "REDEEMED" | "EXPIRED" | "CANCELLED";
  rewardName: string;
  rewardType: string;
  rewardMetadata: { type: string; [k: string]: unknown };
  pointsSpent: number | null;
  stampsSpent: number | null;
  issuedAt: string;
  expiresAt: string;
  redeemedAt: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
  tenantId: string;
  tenantName: string;
}

const STATUS_STYLES: Record<VoucherDetail["status"], string> = {
  ISSUED: "bg-emerald-100 text-emerald-800",
  REDEEMED: "bg-navy/10 text-navy",
  EXPIRED: "bg-ink-light text-ink-dark",
  CANCELLED: "bg-red-100 text-red-800",
};

export function VoucherPage() {
  const { redemptionId = "" } = useParams<{ redemptionId: string }>();
  const { isAuthenticated } = useAuth();
  const [v, setV] = useState<VoucherDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !redemptionId) return;
    let cancelled = false;
    void (async () => {
      const r = await customerFetch<VoucherDetail>(
        `/api/v1/customer/me/redemptions/${redemptionId}`,
      );
      if (cancelled) return;
      if (!r.ok) setErr(r.message);
      else setV(r.data);
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, redemptionId]);

  if (!isAuthenticated) return <Redirect to="/login" />;

  function rewardSummary(): string {
    if (!v) return "";
    const m = v.rewardMetadata as Record<string, unknown>;
    if (m.type === "DISCOUNT_PESO") return `₱${m.amount} off`;
    if (m.type === "DISCOUNT_PERCENT") return `${m.percent}% off`;
    if (m.type === "FREE_ITEM") return `Free ${String(m.itemName ?? "")}`;
    if (m.type === "FREE_VISIT") return "Free visit";
    return v.rewardName;
  }

  return (
    <div className="min-h-screen pb-24">
      <div className="mx-auto max-w-md px-6 pt-8">
        <header className="flex items-center justify-between">
          <Wordmark size="sm" />
          <Link
            href="/profile/vouchers"
            className="text-sm text-ink-dark hover:text-navy"
          >
            All vouchers
          </Link>
        </header>

        {err && (
          <div className="mt-4 rounded-card border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {err}
          </div>
        )}

        {v === null ? (
          <p className="mt-6 text-sm text-ink-dark">Loading…</p>
        ) : (
          <>
            <section className="mt-6 card">
              <p className="text-xs font-semibold uppercase tracking-wider text-ink-dark">
                {v.tenantName}
              </p>
              <h1 className="mt-1 text-2xl font-bold text-navy">
                {rewardSummary()}
              </h1>
              <span
                className={`mt-2 inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${STATUS_STYLES[v.status]}`}
              >
                {v.status}
              </span>
            </section>

            <section className="mt-5 rounded-card border-2 border-dashed border-navy/30 bg-amber-xlight px-4 py-8 text-center">
              <p className="text-xs uppercase tracking-wider text-ink-dark">
                Show this to the cashier
              </p>
              <p className="mt-2 font-mono text-3xl font-bold tracking-widest text-navy break-all">
                {v.voucherCode}
              </p>
            </section>

            <section className="mt-5 card">
              <dl className="grid grid-cols-2 gap-2 text-xs text-ink-dark">
                <dt>Cost</dt>
                <dd>
                  {v.pointsSpent !== null
                    ? `${v.pointsSpent.toFixed(2)} points`
                    : `${v.stampsSpent} stamps`}
                </dd>
                <dt>Issued</dt>
                <dd>{new Date(v.issuedAt).toLocaleString()}</dd>
                <dt>Expires</dt>
                <dd>{new Date(v.expiresAt).toLocaleString()}</dd>
                {v.redeemedAt && (
                  <>
                    <dt>Redeemed</dt>
                    <dd>{new Date(v.redeemedAt).toLocaleString()}</dd>
                  </>
                )}
                {v.cancelledAt && (
                  <>
                    <dt>Cancelled</dt>
                    <dd>{new Date(v.cancelledAt).toLocaleString()}</dd>
                    <dt>Reason</dt>
                    <dd>{v.cancelReason}</dd>
                  </>
                )}
              </dl>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
